<template>
    <div class="border-neutral-200 dark:border-neutral-700 flex items-center"
         data-cy="exercise-row"
         :class="[
           bordered ? 'my-2 border rounded p-3' : compact ? 'px-3 py-2 text-sm' : 'my-2 border-t first:border-t-0 p-2',
         ]"
    >

        <div class="min-w-0 flex-1">

            <template v-if="compact">
                <div class="flex min-h-8 items-center justify-between gap-3">
                    <div class="min-w-0">
                        <div class="flex min-w-0 items-center gap-x-2.5">
                            <div class="truncate" v-if="resistanceSummary">{{ resistanceSummary }}</div>
                            <div class="truncate font-semibold" v-else-if="showMovementName">{{ exercise.movement ? exercise.movement.name : 'Movement' }}</div>
                            <span class="shrink-0 rounded-full bg-brand-primary-900 px-2 py-0.5 text-xs text-white" v-if="exercise.advanced">ADV</span>
                        </div>
                        <div class="mt-1 text-xs text-neutral-600 dark:text-neutral-400" data-cy="exercise-exhaustion-level" v-if="exhaustionLevel">
                            Exhaustion: {{ exercise.exhaustionLevel }}
                        </div>
                    </div>

                    <div class="flex shrink-0 items-center gap-x-3">
                        <div v-if="exercise.duration">
                            <Icon icon="stopwatch"/>
                            {{ $timerValue(exercise.duration) }}
                        </div>

                        <div v-if="exercise.movement && exercise.movement.trackRepetitions && !sortable" class="text-right">
                            <div class="flex justify-end">
                                <div class="font-semibold">{{ exercise.repetitions || 0 }}</div>
                                <div v-if="exercise.movement.trackRepetitionsPartial">
                                    +{{ exercise.partialRepetitions || 0 }}
                                </div>
                            </div>
                            <div class="mt-0.5 text-xs text-neutral-600 dark:text-neutral-400"
                                 data-cy="exercise-previous-reps"
                                 v-if="previousRepetitionSummary"
                            >
                                Prev: {{ previousRepetitionSummary }}
                            </div>
                        </div>

                        <button type="button"
                                aria-label="Delete set"
                                class="flex h-8 w-8 items-center justify-center rounded border border-neutral-300 text-neutral-500 hover:border-red-700 hover:text-red-700 dark:border-neutral-700 dark:text-neutral-400"
                                data-cy="delete-set"
                                title="Delete set"
                                v-if="removable && !sortable"
                                @click.stop="$emit('delete')">
                            <Icon icon="trash-alt"/>
                        </button>
                    </div>
                </div>

                <Notes :notes="exercise.notes" />
            </template>

            <template v-else>
                <div class="flex items-center justify-between">

                    <div class="flex items-center gap-x-2.5">
                        <div class="font-semibold" v-if="showMovementName">{{ exercise.movement ? exercise.movement.name : 'Movement' }}</div>
                        <span class="text-xs bg-brand-primary-900 text-white px-2 py-0.5 rounded-full" v-if="exercise.advanced">ADV</span>
                    </div>

                    <div class="flex items-center gap-x-4">

                        <div v-if="exercise.duration">
                            <Icon icon="stopwatch"/>
                            {{ $timerValue(exercise.duration) }}
                        </div>

                        <div v-if="exercise.movement && exercise.movement.trackRepetitions && !sortable" class="text-right">
                            <div class="flex justify-end">
                                <div class="font-semibold">{{ exercise.repetitions || 0 }}</div>
                                <div v-if="exercise.movement.trackRepetitionsPartial">
                                    +{{ exercise.partialRepetitions || 0 }}
                                </div>
                            </div>
                            <div class="mt-0.5 text-xs text-neutral-600 dark:text-neutral-400"
                                 data-cy="exercise-previous-reps"
                                 v-if="previousRepetitionSummary"
                            >
                                Prev: {{ previousRepetitionSummary }}
                            </div>
                        </div>

                    </div>

                </div>

                <div class="flex items-center justify-between" v-if="exercise.movement">

                    <div class="flex gap-x-2.5">

                        <div v-if="exercise.movement.trackResistance">

                            <div v-if="exercise.movement.resistanceType === 'band'">
                                {{
                                    exercise.bands && exercise.bands.length ? exercise.bands.map(b => b.brandAndName).join(', ') : 'No Band Selected'
                                }}
                            </div>

                            <div v-else-if="weight">
                                {{ weight }}
                            </div>

                        </div>

                    </div>

                </div>

                <div class="mt-2 text-sm text-neutral-600 dark:text-neutral-400" data-cy="exercise-exhaustion-level" v-if="exhaustionLevel">
                    Exhaustion: {{ exercise.exhaustionLevel }}
                </div>

                <Notes :notes="exercise.notes" />
            </template>

        </div>

        <div class="drag-handle rounded border border-neutral-500 px-5 py-2 ml-4" v-if="sortable">
            <Icon icon="sort"/>
        </div>

    </div>
</template>

<script>
import {defineComponent} from 'vue';
import Notes from '@/components/Exercise/Notes.vue';

export default defineComponent({
  name: 'Row',
  components: {
    Notes,
  },
  emits: [
    'delete',
  ],
  computed: {
    user() {
      return this.$store.getters['auth/user'];
    },
    weight() {
      if (!this.exercise.weight) {
        return null;
      }

      const displayUnit = this.weightDisplayUnit;
      const displayWeight = this.$convert(this.exercise.weight, 'kg').to(displayUnit);
      const displayFormat = this.user.systemOfMeasure === 'metric' ? '0,0.0' : '0,0';
      return `${this.$numeral(displayWeight).format(displayFormat)} ${displayUnit}`;
    },
    exhaustionLevel() {
      return this.exercise.exhaustionLevel || null;
    },
    resistanceSummary() {
      if (!this.exercise.movement || !this.exercise.movement.trackResistance) {
        return null;
      }

      if (this.exercise.movement.resistanceType === 'band') {
        return this.exercise.bands && this.exercise.bands.length ? this.exercise.bands.map(b => b.brandAndName).join(', ') : 'No Band Selected';
      }

      return this.weight;
    },
    previousRepetitionSummary() {
      if (!this.lastExercise || !this.exercise.movement || !this.exercise.movement.trackRepetitions) {
        return null;
      }

      const repetitions = this.lastExercise.repetitions;
      const partialRepetitions = this.lastExercise.partialRepetitions;
      const hasRepetitions = repetitions !== undefined && repetitions !== null;
      const hasPartialRepetitions = partialRepetitions !== undefined && partialRepetitions !== null
        && this.exercise.movement.trackRepetitionsPartial;

      if (!hasRepetitions && !hasPartialRepetitions) {
        return null;
      }

      if (hasPartialRepetitions) {
        return `${hasRepetitions ? repetitions : 0} +${partialRepetitions}`;
      }

      return repetitions;
    },
    weightDisplayUnit() {
      return this.user.systemOfMeasure === 'metric' ? 'kg' : 'lb';
    },
  },
  props: {
    bordered: {
      default: false,
      required: false,
    },
    compact: {
      default: false,
      required: false,
    },
    exercise: {
      required: true,
    },
    lastExercise: {
      default: null,
      required: false,
    },
    removable: {
      default: false,
      required: false,
      type: Boolean,
    },
    showMovementName: {
      default: true,
      required: false,
    },
    sortable: {
      default: false,
      required: false,
    },
  },
});
</script>

<style scoped lang="scss">

</style>
